import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { solution, features } from '@/lib/marketing-data';
import {
  ArrowRight,
  Shield,
  Zap,
  Lock,
  Cloud,
  Server,
  Database,
  GitBranch,
  Layers,
  FileText,
  Network,
  MessageSquare,
  FolderGit2,
  Puzzle,
} from 'lucide-react';

export const Route = createFileRoute('/platform')({
  component: PlatformPage,
});

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  GitBranch,
  Layers,
  FileText,
  Shield,
  Network,
  MessageSquare,
  FolderGit2,
  Puzzle,
};

function PlatformPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                The <span className="text-primary">Forge Pipeline</span>
              </h1>
              <p className="mt-6 text-xl text-muted-foreground">
                A unified platform that transforms how engineering teams understand,
                document, and evolve their software.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register">
                  <Button size="lg" className="gap-2">
                    Start Free <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                {solution.title}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                {solution.description}
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-4">
              {solution.steps.map((step, index) => (
                <div key={step.number} className="relative">
                  {index < solution.steps.length - 1 && (
                    <div className="absolute top-12 left-[calc(50%+2rem)] hidden w-[calc(100%-4rem)] border-t-2 border-dashed border-border lg:block" />
                  )}
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-bold relative z-10">
                      {step.number}
                    </div>
                    <h3 className="mt-6 text-xl font-semibold">{step.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Architecture */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Built for Scale
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Enterprise-grade architecture designed for security, performance, and
                reliability.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: Cloud,
                  title: 'Cloud Native',
                  description:
                    'Kubernetes-native deployment with auto-scaling and self-healing capabilities.',
                },
                {
                  icon: Lock,
                  title: 'Security First',
                  description:
                    'End-to-end encryption, SOC 2 compliance, and zero-trust architecture.',
                },
                {
                  icon: Zap,
                  title: 'High Performance',
                  description:
                    'Sub-second analysis times powered by optimized AI inference pipelines.',
                },
                {
                  icon: Server,
                  title: 'Self-Hosted Option',
                  description:
                    'Deploy in your own cloud or on-premises for complete data sovereignty.',
                },
                {
                  icon: Database,
                  title: 'Data Isolation',
                  description:
                    'Multi-tenant architecture with complete data isolation between organizations.',
                },
                {
                  icon: Shield,
                  title: '99.9% Uptime SLA',
                  description:
                    'Enterprise SLA with financial backing for mission-critical deployments.',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-xl border bg-card p-6 hover:border-primary/50 transition-colors"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{feature.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Platform Capabilities
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Everything you need to transform your development workflow.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => {
                const Icon = iconMap[feature.icon] || Layers;
                return (
                  <div
                    key={feature.title}
                    className="rounded-xl border bg-card p-6 hover:shadow-lg transition-all"
                  >
                    <Icon className="h-8 w-8 text-primary" />
                    <h3 className="mt-4 font-semibold">{feature.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary p-8 sm:p-16 text-center">
              <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                Ready to Transform Your Workflow?
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
                Join engineering teams already using ForgeNetwork to ship better
                software, faster.
              </p>
              <div className="mt-8">
                <Link to="/register">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="gap-2 bg-white text-primary hover:bg-white/90"
                  >
                    Start Free Today <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
